import * as React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import isEmpty from 'lodash/isEmpty'

import config from '../../config'

import useAsync from '../../../../commons/src/hooks/use-async'
import useAttachments from '../../../../commons/src/hooks/use-attachments'
import {captureException} from '../../../../commons/src/services/logging'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import useSelector from '../../hooks/use-selector'
import {formatFileSize} from '../../libs/formatter'
import * as providerProfileImagesApi from '../../services/api/provider-profile-image'

import {AttachmentFile} from '../../../../commons/src/types'
import {ProviderId, ProviderProfileImage} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import deleteIcon from '../../../../commons/src/assets/icons/delete.svg'
import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'
import uploadBoxIcon from '../../../../commons/src/assets/icons/upload-box.svg'
import fallbackSource from '../../../../commons/src/assets/images/model-thumbnail-fallback.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import Grid from '../../../../commons/src/components/grid'
import GridCell from '../../../../commons/src/components/grid/GridCell'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import ImageContainer from '../../../../commons/src/components/image-container'
import ImageHoverInteraction from '../../../../commons/src/components/image-hover-interaction'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'

type Props = {
  providerId: ProviderId
}

const SettingsProfileImagesPartial = ({providerId}: Props) => {
  const [isProviderProfileImagesSubmitted, setIsProviderProfileImagesSubmitted] =
    React.useState(false)

  const {attachments, fileSizeExceededCount, addAttachments, deleteAttachment, resetAttachments} =
    useAttachments(config.fileSizeLimit, true)

  const profileImagesInputRef = React.useRef<HTMLInputElement | null>(null)

  const provider = useSelector(state => state.core.provider)
  invariant(provider, 'Provider missing.')

  const {
    isLoading: isLoadingProviderProfileImages,
    data: providerProfileImages = [],
    refresh: receiveProviderProfileImages,
    error: providerProfileImagesError,
  } = useAsync(() => providerProfileImagesApi.getByProvider(provider.id), [], {
    onError: error => {
      captureException(error)
    },
  })

  const providerProfileImagesFormik = useFormik<{
    images: ProviderProfileImage[]
    formikGeneral: string
  }>({
    enableReinitialize: true,
    initialValues: {
      images: providerProfileImages,
      formikGeneral: '',
    },
    onSubmit: async (values, actions) => {
      const imageIdsToDelete = providerProfileImages
        .filter(profileImage => !values.images.find(image => image.id === profileImage.id))
        .map(image => image.id)

      const files = Object.values(attachments).map(file => file.file)

      if (files.length === 0 && values.images.length === 0) {
        actions.setFieldError('formikGeneral', 'At least one profile image is required.')
        return
      }

      try {
        if (files.length > 0) {
          await providerProfileImagesApi.upload({file: files, provider: providerId})
        }
        if (imageIdsToDelete.length > 0) {
          await providerProfileImagesApi.removeMultiple(imageIdsToDelete)
        }

        await receiveProviderProfileImages()

        setIsProviderProfileImagesSubmitted(true)

        actions.resetForm()
        resetAttachments()
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', `Update failed with: ${(error as Error).message}`)
      }
    },
  })

  const attachmentsCount = Object.values(attachments).length
  const formikImagesCount = providerProfileImagesFormik.values.images.length

  const onProfileImagesInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      if (event.target.files.length + formikImagesCount > config.profileImagesMaxCount) {
        providerProfileImagesFormik.setFieldError(
          'formikGeneral',
          `Please be aware that we only allow a maximum of ${config.profileImagesMaxCount} profile images.`
        )
      } else {
        addAttachments([...event.target.files])
        providerProfileImagesFormik.setFieldError('formikGeneral', '')
        setIsProviderProfileImagesSubmitted(false)
      }
    }
    // Clear file input
    event.target.value = ''
  }

  const renderProfileImage = (image: ProviderProfileImage) => (
    <ImageHoverInteraction
      hoverContent={
        <Button
          hierarchy="secondary"
          icon={<Icon source={deleteIcon} />}
          onClick={() => {
            providerProfileImagesFormik.setFieldValue(
              'images',
              providerProfileImagesFormik.values.images.filter(
                profileImage => profileImage.id !== image.id
              )
            )
            setIsProviderProfileImagesSubmitted(false)
          }}
          disabled={providerProfileImagesFormik.isSubmitting}
        >
          Delete
        </Button>
      }
    >
      <ImageContainer
        ratio="1-1"
        source={image.url}
        fallbackSource={fallbackSource}
        alt={`Preview image of ${image.name}`}
      />
    </ImageHoverInteraction>
  )

  const renderProfileImageFile = (file: AttachmentFile) => (
    <ImageHoverInteraction
      hoverContent={
        <Button
          hierarchy="secondary"
          icon={<Icon source={deleteIcon} />}
          onClick={() => {
            deleteAttachment(file.id)
            providerProfileImagesFormik.setFieldValue('formikGeneral', '')
            setIsProviderProfileImagesSubmitted(false)
          }}
          disabled={providerProfileImagesFormik.isSubmitting}
        >
          Delete
        </Button>
      }
    >
      <ImageContainer
        ratio="1-1"
        source={file.src}
        fallbackSource={fallbackSource}
        alt={`Preview image of ${file.file.name}`}
      />
    </ImageHoverInteraction>
  )

  return (
    <Section>
      <Stack>
        <SplitLayout
          left={
            <Headline
              label="Profile images"
              classNames={['u-no-margin-bottom']}
              addendum={`(${attachmentsCount + formikImagesCount}/${config.profileImagesMaxCount})`}
            />
          }
          right={
            <>
              <Button
                hierarchy="secondary"
                icon={<Icon source={uploadBoxIcon} />}
                onClick={() => profileImagesInputRef.current?.click()}
                disabled={
                  providerProfileImagesFormik.isSubmitting ||
                  attachmentsCount + formikImagesCount === config.profileImagesMaxCount
                }
              >
                Add
              </Button>
              <input
                type="file"
                id="logo-input"
                accept="image/png, image/jpeg"
                multiple
                ref={profileImagesInputRef}
                style={{display: 'none'}}
                onChange={onProfileImagesInputChange}
                disabled={providerProfileImagesFormik.isSubmitting}
              />
            </>
          }
        />
        <Paragraph classNames={['u-no-margin-bottom']}>
          Add profile images that will be displayed on your Craftcloud profile.
        </Paragraph>
        {isLoadingProviderProfileImages && (
          <div>
            <LoadingIndicator />
          </div>
        )}
        {!isLoadingProviderProfileImages && providerProfileImagesError && (
          <ActionBar>
            <Paragraph size="xs" warning classNames={['u-no-margin-bottom']}>
              Loading your current profile images failed
            </Paragraph>
            <LinkButton
              icon={<Icon source={refreshIcon} />}
              onClick={() => {
                receiveProviderProfileImages()
              }}
            />
          </ActionBar>
        )}
        {!isLoadingProviderProfileImages && !providerProfileImagesError && (
          <form onSubmit={providerProfileImagesFormik.handleSubmit}>
            <Stack>
              {formikImagesCount === 0 && attachmentsCount === 0 && (
                <Paragraph minor size="s">
                  No profile images added
                </Paragraph>
              )}
              <Grid>
                {providerProfileImagesFormik.values.images.map(image => (
                  <GridCell m={4} l={3} key={image.id}>
                    {renderProfileImage(image)}
                  </GridCell>
                ))}
                {attachmentsCount > 0 &&
                  Object.values(attachments).map((file, index) => (
                    <GridCell m={4} l={3} key={file.id}>
                      {renderProfileImageFile(file)}
                    </GridCell>
                  ))}
              </Grid>
              {providerProfileImagesFormik.errors.formikGeneral && (
                <Paragraph error>{providerProfileImagesFormik.errors.formikGeneral}</Paragraph>
              )}
              {fileSizeExceededCount > 0 && (
                <Paragraph
                  size="s"
                  warning
                >{`We could not add ${fileSizeExceededCount} of your selected files as the file size is exceeding our file size limit of ${formatFileSize(
                  config.fileSizeLimit,
                  0
                )}.`}</Paragraph>
              )}
            </Stack>
          </form>
        )}
        <ActionBar>
          <Button
            disabled={
              providerProfileImagesFormik.isSubmitting ||
              isProviderProfileImagesSubmitted ||
              !(providerProfileImagesFormik.dirty || attachmentsCount > 0)
            }
            icon={
              isProviderProfileImagesSubmitted ? (
                <Icon source={checkedIcon} />
              ) : providerProfileImagesFormik.isSubmitting &&
                providerProfileImagesFormik.submitCount > 0 &&
                isEmpty(providerProfileImagesFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => providerProfileImagesFormik.handleSubmit())}
          >
            {isProviderProfileImagesSubmitted ? 'Saved' : 'Save'}
          </Button>
          <Button
            hierarchy="secondary"
            disabled={
              providerProfileImagesFormik.isSubmitting ||
              isProviderProfileImagesSubmitted ||
              !(providerProfileImagesFormik.dirty || attachmentsCount > 0)
            }
            onClick={preventDoubleClick(() => {
              providerProfileImagesFormik.resetForm()
              resetAttachments()
            })}
          >
            Reset
          </Button>
        </ActionBar>
      </Stack>
    </Section>
  )
}

export default SettingsProfileImagesPartial
